/*
  Standalone host: a plain front-end build with no shell.

  There is no outbox to route through and no signer, so this host reads the
  problem tree straight from a fixed set of relays and does no composing at
  all. It is the same app (`src/app.ts`) with a different capability provider —
  see `src/host/types.ts`.

  Relays are read in two tiers: the defaults, then a documented fallback set
  when the defaults have nothing for this tree (relays drop and purge; see
  `web-relay.ts`). Passing relays explicitly (`?relays=` / `VITE_RELAYS`)
  replaces both tiers with exactly what was asked for.
*/
import type { EventSubscription, NostrFilter, RelayEventResult } from "../core/nostr";
import { problemRootFilters, problemTreeFilters, summarizeRoots, type RootSummary } from "../core/problem-dag";
import { rocketFilters } from "../core/rockets";
import {
  DEFAULT_QUERY_TIMEOUT_MS, DEFAULT_RELAYS, FALLBACK_RELAYS, relayQuery, relayWatch
} from "./web-relay";
import type { HostActionResult, HostCapabilities, ProblemTreeHost } from "./types";

export interface WebHostOptions {
  /** Read set. Defaults to {@link DEFAULT_RELAYS}. */
  relays?: readonly string[];
  /** Consulted only when the read set returns nothing. Defaults to {@link FALLBACK_RELAYS}. */
  fallbackRelays?: readonly string[];
  /** Called after a root loads, so the page can make it shareable in the URL. */
  onRootChange?(rootCoordinate: string): void;
}

const READ_ONLY = "Read-only build: this page has no signer, so problems open inside a Napplet shell.";

export function createWebHost(options: WebHostOptions = {}): ProblemTreeHost {
  const primary = options.relays?.length ? [...options.relays] : [...DEFAULT_RELAYS];
  const fallback = (options.fallbackRelays ?? (options.relays?.length ? [] : FALLBACK_RELAYS))
    .filter((relay) => !primary.includes(relay));
  // Live updates come from both tiers: whichever relay is up keeps streaming.
  const watchRelays = [...primary, ...fallback];
  let sourceLabel = "the default relays";

  /** Read the default relays first, and only widen if they had nothing to say. */
  async function read(filters: readonly NostrFilter[]): Promise<RelayEventResult[]> {
    try {
      const events = await relayQuery(primary, filters, { timeoutMs: DEFAULT_QUERY_TIMEOUT_MS });
      if (events.length) {
        sourceLabel = "the default relays";
        console.info(`Read ${events.length} event(s) from ${primary.join(", ")}`);
        return events;
      }
      console.info(`No matching events on ${primary.join(", ")}`);
    } catch (error) {
      console.warn(`Default relays failed (${primary.join(", ")})`, { error });
    }
    if (!fallback.length) return [];
    const events = await relayQuery(fallback, filters, { timeoutMs: DEFAULT_QUERY_TIMEOUT_MS });
    sourceLabel = "the fallback relays";
    console.info(`Read ${events.length} event(s) from the fallback relays ${fallback.join(", ")}`);
    return events;
  }

  return {
    kind: "web",
    get sourceLabel() {
      return sourceLabel;
    },

    async capabilities(): Promise<HostCapabilities> {
      // Nothing here dispatches intents: the tree is read-only on the web.
      return { viewProblem: false, composeProblem: false, viewRocket: false };
    },

    async discoverRoots(): Promise<RootSummary[]> {
      return summarizeRoots(await read(problemRootFilters()));
    },

    queryTree(rootCoordinate: string): Promise<RelayEventResult[]> {
      return read(problemTreeFilters(rootCoordinate));
    },

    queryRockets(): Promise<RelayEventResult[]> {
      return read(rocketFilters());
    },

    watchTree(rootCoordinate: string, handlers): EventSubscription {
      return relayWatch(watchRelays, problemTreeFilters(rootCoordinate), {
        onEvent: (result) => handlers.onEvents([result]),
        onClosed: (reason) => handlers.onClosed(reason),
        onNotice: (message) => console.warn(`Relay notice: ${message}`)
      });
    },

    async viewProblem(): Promise<HostActionResult> {
      return { ok: false, error: READ_ONLY };
    },

    async viewRocket(): Promise<HostActionResult> {
      return { ok: false, error: READ_ONLY };
    },

    async composeChildProblem(): Promise<HostActionResult> {
      return { ok: false, error: READ_ONLY };
    },

    async composeTopLevelProblem(): Promise<HostActionResult> {
      return { ok: false, error: READ_ONLY };
    },

    rememberRoot: options.onRootChange
  };
}
